import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './Admin.css';
import PopupAdmin from './PopupAdmin';
import AdminPassword from './AdminPassword';
import AdminContent from './AdminContent';
import AdminAccess from './AdminAccess';
import { useLang } from '../LanguageContext';

// Enter 시 <p> 대신 <br> 줄바꿈 사용
const Block = Quill.import('blots/block');
Block.tagName = 'DIV';
Quill.register(Block, true);

function Linkify({ text }) {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{color:'var(--primary)', wordBreak:'break-all'}}>{part}</a>
      : part
  );
}

// 글 구분: PRESS=보도자료 / NOTICE=공지사항 (서버 normCategory 와 같은 값)
const NAV = [
  ['notice',   '게시글',       '\u{1F4C4}'],
  ['inquiry',  '문의',         '\u{2709}\u{FE0F}'],
  ['popup',    '팝업',         '\u{1F5D4}\u{FE0F}'],
  ['content',  '콘텐츠 관리',   '\u{1F4DA}'],
  ['access',   '접속 기록',     '\u{1F6E1}\u{FE0F}'],
  ['password', '비밀번호 변경', '\u{1F512}'],
];
const NAV_TITLE = {
  notice: '게시글 관리', inquiry: '문의 관리', popup: '팝업 관리',
  content: '콘텐츠 관리', access: '접속 기록', password: '계정 설정',
};

const EMPTY_FORM = {
  title: '', title_en: '', content: '', content_en: '', author: 'medilogis',
  category: 'PRESS', video_url: '', file_url: '', file_name: '', is_pinned: false,
};

function AdminEditor({ content, onChange, token }) {
  const quillRef = useRef(null);

  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = () => {
      const file = input.files[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('image', file);
      fetch('/api/admin/upload', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token },
        body: formData,
      })
        .then(res => res.json())
        .then(data => {
          if (data.success && quillRef.current) {
            const editor = quillRef.current.getEditor();
            const range = editor.getSelection(true);
            editor.insertEmbed(range.index, 'image', data.url);
            editor.setSelection(range.index + 1);
          } else {
            alert(data.error || '업로드 실패');
          }
        })
        .catch(() => alert('업로드 실패'));
    };
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image: imageHandler,
      },
    },
    keyboard: {
      bindings: {
        enter: {
          key: 13,
          handler: function(range) {
            this.quill.insertText(range.index, '\n');
            this.quill.setSelection(range.index + 1);
            return false;
          }
        }
      }
    }
  }), [token]);

  return (
    <ReactQuill
      ref={quillRef}
      theme="snow"
      value={content}
      onChange={onChange}
      modules={modules}
      style={{ minHeight: '300px' }}
    />
  );
}

// 콘솔 공통 껍데기 — 목록·글쓰기·상세가 같은 사이드바를 쓴다
function AdminShell({ tab, onTab, noticeCount, newInquiries, me, lang, setLang, onLogout, title, actions, children }) {
  return (
    <div className="ad-shell">
      <aside className="ad-side">
        <div className="ad-brand">
          <span className="ad-dot" />
          <span className="ad-name">MEDILOGIS</span>
          <em>관리자</em>
        </div>
        <nav className="ad-nav">
          {NAV.map(([key, label, icon]) => (
            <button key={key} type="button" className={tab === key ? 'on' : ''} onClick={() => onTab(key)}>
              <span className="ic" aria-hidden="true">{icon}</span>
              <span className="tx">{label}</span>
              {key === 'notice' && <span className="ct">{noticeCount}</span>}
              {key === 'inquiry' && newInquiries > 0 && <span className="ct alert">{newInquiries}</span>}
            </button>
          ))}
        </nav>
        <a className="ad-site" href="/" target="_blank" rel="noopener noreferrer">사이트 보기 ↗</a>
      </aside>
      <div className="ad-main">
        <header className="ad-top">
          <h2>{title}</h2>
          <div className="ad-top-right">
            <div className="admin-lang-toggle" title="미리보기 언어 (저장된 내용 확인용)">
              <button type="button" className={lang === 'ko' ? 'on' : ''} onClick={() => setLang('ko')}>KO</button>
              <button type="button" className={lang === 'en' ? 'on' : ''} onClick={() => setLang('en')}>EN</button>
            </div>
            {actions}
            <span className="ad-who">{(me && (me.NAME || me.ID)) || '관리자'}</span>
            <button className="admin-btn-logout" onClick={onLogout}>로그아웃</button>
          </div>
        </header>
        <div className="ad-content">{children}</div>
      </div>
    </div>
  );
}

function Admin() {
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [loginId, setLoginId] = useState('');
  const [loginPw, setLoginPw] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);   // 중복 전송 차단 — 한 번 클릭에 두 번 가면 5회 제한이 금방 찬다
  const [lockLeft, setLockLeft] = useState(0);         // 잠금 남은 초
  const [notices, setNotices] = useState([]);
  const [editMode, setEditMode] = useState(null); // null=list, 'new'=신규, {IDX}=수정
  const [viewNotice, setViewNotice] = useState(null); // 상세보기
  const [adminSearch, setAdminSearch] = useState('');
  const [adminSearchType, setAdminSearchType] = useState('title');
  const [adminCat, setAdminCat] = useState('ALL');   // ALL | PRESS | NOTICE
  const [adminTab, setAdminTab] = useState('notice'); // 'notice' or 'inquiry'
  const [inquiries, setInquiries] = useState([]);
  const [me, setMe] = useState(null);
  const [viewInquiry, setViewInquiry] = useState(null);
  const [adminPage, setAdminPage] = useState(1);
  const ADMIN_PAGE_SIZE = 15;
  const { lang, setLang } = useLang();
  // 현재 미리보기 언어에 맞춰 제목·내용 선택
  const pickTitle = (n) => (lang === 'en' && n.TITLE_EN ? n.TITLE_EN : n.TITLE);
  const pickContent = (n) => (lang === 'en' && n.CONTENT_EN ? n.CONTENT_EN : n.CONTENT);
  const [form, setForm] = useState(EMPTY_FORM);

  const headers = { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token };

  // 뒤로가기 지원
  const pushState = (view) => {
    window.history.pushState({ view }, '');
  };

  useEffect(() => {
    const handlePop = () => {
      setViewNotice(null);
      setViewInquiry(null);
      setEditMode(null);
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginBusy || lockLeft > 0) return;    // 응답 오기 전 재전송 금지
    setLoginBusy(true);
    fetch('/api/admin/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: loginId, password: loginPw }),
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setToken(data.token);
          localStorage.setItem('admin_token', data.token);
          setLoginError('');
          setLockLeft(0);
        } else if (data.locked) {
          // 비번이 맞아도 잠금이면 서버가 검사 자체를 안 한다 — 그걸 알려줘야 더 시도하지 않는다
          setLockLeft(data.retryAfter || 600);
          setLoginError('');
        } else {
          setLoginError('아이디 또는 비밀번호가 올바르지 않습니다.');
        }
      })
      .catch(() => setLoginError('서버 연결 실패'))
      .finally(() => setLoginBusy(false));
  };

  // 잠금 남은 시간 카운트다운
  useEffect(() => {
    if (lockLeft <= 0) return;
    const t = setInterval(() => setLockLeft((v) => (v <= 1 ? 0 : v - 1)), 1000);
    return () => clearInterval(t);
  }, [lockLeft]);

  const handleDeleteInquiry = (idx) => {
    if (!window.confirm('이 문의를 삭제하시겠습니까?\n삭제하면 되돌릴 수 없습니다.')) return;
    fetch(`/api/admin/inquiry/${idx}`, { method: 'DELETE', headers })
      .then((r) => r.json())
      .then(() => { setViewInquiry(null); loadInquiries(); })
      .catch(() => alert('삭제하지 못했습니다.'));
  };

  const handleLogout = () => {
    // 서버에서도 토큰을 무효화한다 — 지우기만 하면 그 토큰은 만료까지 살아 있다
    fetch('/api/admin/logout', { method: 'POST', headers }).catch(() => {});
    setToken('');
    localStorage.removeItem('admin_token');
  };

  const newInquiries = inquiries.filter(x => x.STATUS === 0).length;

  const shellProps = {
    tab: adminTab,
    onTab: (k) => { setAdminTab(k); setEditMode(null); setViewNotice(null); setViewInquiry(null); if (k === 'inquiry') loadInquiries(); },
    noticeCount: notices.length,
    newInquiries,
    me, lang, setLang, onLogout: handleLogout,
  };

  const loadNotices = () => {
    fetch('/api/admin/notices', { headers })
      .then(res => {
        if (res.status === 401) { handleLogout(); return []; }
        return res.json();
      })
      .then(data => { if (Array.isArray(data)) setNotices(data); })
      .catch(() => {});
  };

  const loadInquiries = () => {
    fetch('/api/admin/inquiries', { headers })
      .then(res => res.json())
      .then(data => { if (Array.isArray(data)) setInquiries(data); })
      .catch(() => {});
  };

  useEffect(() => {
    if (token) {
      loadNotices(); loadInquiries();
      fetch('/api/admin/me', { headers }).then(r => (r.ok ? r.json() : null))
        .then(d => { if (d && d.ID) setMe(d); }).catch(() => {});
    }
  }, [token]);

  const handleSave = (e) => {
    e.preventDefault();
    const isNew = editMode === 'new';
    const url = isNew ? '/api/admin/notice' : `/api/admin/notice/${editMode}`;
    const method = isNew ? 'POST' : 'PUT';

    fetch(url, { method, headers, body: JSON.stringify(form) })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          const savedIdx = isNew ? data.idx : editMode;
          setEditMode(null);
          setForm(EMPTY_FORM);
          // 목록 새로고침 후 상세보기로 이동
          fetch('/api/admin/notices', { headers })
            .then(res => res.json())
            .then(list => {
              if (Array.isArray(list)) {
                setNotices(list);
                const saved = list.find(n => n.IDX === savedIdx);
                if (saved) setViewNotice(saved);
              }
            });
        }
      })
      .catch(() => {});
  };

  const handleDelete = (idx) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    fetch(`/api/admin/notice/${idx}`, { method: 'DELETE', headers })
      .then(res => res.json())
      .then(data => { if (data.success) loadNotices(); })
      .catch(() => {});
  };

  const handleEdit = (notice) => {
    setEditMode(notice.IDX);
    setForm({
      title: notice.TITLE,
      title_en: notice.TITLE_EN || '',
      content: notice.CONTENT,
      content_en: notice.CONTENT_EN || '',
      author: notice.AUTHOR || 'medilogis',
      video_url: notice.VIDEO_URL || '',
      file_url: notice.FILE_URL || '',
      file_name: notice.FILE_NAME || '',
      category: notice.CATEGORY || 'PRESS',
      is_pinned: notice.IS_PINNED === 1,
    });
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // 로그인 화면
  if (!token) {
    return (
      <div className="admin-login-wrap">
        <form className="admin-login" onSubmit={handleLogin}>
          <h2>관리자 로그인</h2>
          <div className="admin-field">
            <label>아이디</label>
            <input type="text" value={loginId} onChange={e => setLoginId(e.target.value)} autoFocus />
          </div>
          <div className="admin-field">
            <label>비밀번호</label>
            <input type="password" value={loginPw} onChange={e => setLoginPw(e.target.value)} />
          </div>
          {loginError && <p className="admin-error">{loginError}</p>}
          {lockLeft > 0 && (
            <p className="admin-error">
              로그인 시도가 너무 많습니다. {Math.floor(lockLeft / 60)}분 {lockLeft % 60}초 후 다시 시도해 주세요.
            </p>
          )}
          <button type="submit" className="admin-btn-primary" disabled={loginBusy || lockLeft > 0}>
            {lockLeft > 0 ? '잠시 후 다시 시도' : loginBusy ? '확인 중…' : '로그인'}
          </button>
        </form>
      </div>
    );
  }

  // 글쓰기/수정 화면
  if (editMode) {
    return (
      <AdminShell {...shellProps} title={editMode === 'new' ? '글 등록' : '글 수정'}>
        <form className="admin-form" onSubmit={handleSave}>
          <div className="admin-pin-toggle">
            <input type="checkbox" id="pin-check" checked={form.is_pinned} onChange={e => setForm({ ...form, is_pinned: e.target.checked })} />
            <label htmlFor="pin-check">공지로 등록 (상위 고정)</label>
          </div>
          <div className="admin-field">
            <label>구분</label>
            <div className="admin-cat">
              {[['PRESS', '보도자료'], ['NOTICE', '공지사항']].map(([v, ko]) => (
                <label key={v} className={form.category === v ? 'on' : ''}>
                  <input
                    type="radio"
                    name="category"
                    value={v}
                    checked={form.category === v}
                    onChange={() => setForm({ ...form, category: v })}
                  />
                  {ko}
                </label>
              ))}
            </div>
          </div>
          <div className="admin-field">
            <label>제목 <span style={{color:'#999', fontWeight:'normal', fontSize:'12px'}}>(한국어)</span></label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
          </div>
          <div className="admin-field">
            <label>Title <span style={{color:'#999', fontWeight:'normal', fontSize:'12px'}}>(English — optional, 비우면 한국어로 표시)</span></label>
            <input type="text" value={form.title_en} onChange={e => setForm({ ...form, title_en: e.target.value })} placeholder="English title" />
          </div>
          <div className="admin-field">
            <label>작성자</label>
            <input type="text" value={form.author} onChange={e => setForm({ ...form, author: e.target.value })} />
          </div>
          <div className="admin-field">
            <label>내용 <span style={{color:'#999', fontWeight:'normal', fontSize:'12px'}}>(한국어 · 이미지는 툴바의 사진 아이콘으로 삽입)</span></label>
            <AdminEditor content={form.content} onChange={(val) => setForm(prev => ({ ...prev, content: val }))} token={token} />
          </div>
          <div className="admin-field">
            <label>Content <span style={{color:'#999', fontWeight:'normal', fontSize:'12px'}}>(English — optional)</span></label>
            <AdminEditor content={form.content_en} onChange={(val) => setForm(prev => ({ ...prev, content_en: val }))} token={token} />
          </div>
          <div className="admin-field">
            <label>동영상 URL (선택, YouTube)</label>
            <input type="text" value={form.video_url} onChange={e => setForm({ ...form, video_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." />
          </div>
          <div className="admin-field">
            <label>첨부파일 (선택)</label>
            <div className="admin-upload-row">
              <input
                type="file"
                accept=".pdf,.hwp,.hwpx,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt,.jpg,.jpeg,.png"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const formData = new FormData();
                  formData.append('file', file);
                  fetch('/api/admin/upload', {
                    method: 'POST',
                    headers: { 'Authorization': 'Bearer ' + token },
                    body: formData,
                  })
                    .then(res => res.json())
                    .then(data => {
                      if (data.success) {
                        setForm(prev => ({ ...prev, file_url: data.url, file_name: data.originalName }));
                      } else {
                        alert(data.error || '업로드 실패');
                      }
                    })
                    .catch(() => alert('업로드 실패'));
                }}
              />
              {form.file_name && (
                <div className="admin-file-info">
                  <span>📎 {form.file_name}</span>
                  <button type="button" className="admin-btn-sm admin-btn-del" onClick={() => setForm({ ...form, file_url: '', file_name: '' })}>삭제</button>
                </div>
              )}
            </div>
          </div>
          <div className="admin-actions">
            <button type="submit" className="admin-btn-primary">{editMode === 'new' ? '등록' : '수정 완료'}</button>
            <button type="button" className="admin-btn-cancel" onClick={() => { setEditMode(null); setForm(EMPTY_FORM); }}>취소</button>
          </div>
        </form>
      </AdminShell>
    );
  }

  // 상세보기 화면
  if (viewNotice) {
    const currentIdx = notices.findIndex(n => n.IDX === viewNotice.IDX);
    const prevNotice = currentIdx > 0 ? notices[currentIdx - 1] : null;
    const nextNotice = currentIdx < notices.length - 1 ? notices[currentIdx + 1] : null;

    return (
      <AdminShell {...shellProps} title="글 보기">
        <div className="admin-detail">
          <div className="admin-detail-header">
            <h3>{pickTitle(viewNotice)}</h3>
            <div className="admin-detail-meta">
              <span>작성자: {viewNotice.AUTHOR || 'medilogis'}</span>
              <span>날짜: {formatDate(viewNotice.CREATED_AT)}</span>
              <span>조회: {viewNotice.VIEW_COUNT || 0}</span>
            </div>
            {viewNotice.FILE_URL && (
            <div className="admin-detail-file">
              <span className="admin-detail-file-label">첨부파일</span>
              <a href={viewNotice.FILE_URL} download={viewNotice.FILE_NAME || true}>{viewNotice.FILE_NAME || '첨부파일 다운로드'}</a>
            </div>
            )}
          </div>
          <div className="admin-detail-content">
            {(pickContent(viewNotice) || '').includes('<') ? (
              <div dangerouslySetInnerHTML={{ __html: pickContent(viewNotice) }} className="admin-html-content" />
            ) : (
              <p><Linkify text={pickContent(viewNotice)} /></p>
            )}
            {viewNotice.VIDEO_URL && (
              <div className="admin-detail-video">
                <iframe
                  src={viewNotice.VIDEO_URL.replace('watch?v=', 'embed/')}
                  title="video"
                  allowFullScreen
                  frameBorder="0"
                ></iframe>
              </div>
            )}
          </div>
          <div className="admin-actions" style={{justifyContent:'flex-end', marginBottom:16}}>
            <button className="admin-btn-primary" onClick={() => { pushState('edit'); setViewNotice(null); handleEdit(viewNotice); }}>수정</button>
            <button className="admin-btn-cancel" onClick={() => setViewNotice(null)}>목록</button>
          </div>
          <table className="admin-nav-table">
            <tbody>
              {prevNotice && (
                <tr className="admin-nav-row" onClick={() => { pushState('view'); setViewNotice(prevNotice); }}>
                  <td className="admin-nav-label">이전</td>
                  <td className="admin-nav-title">{pickTitle(prevNotice)}</td>
                  <td className="admin-nav-author">{prevNotice.AUTHOR || 'medilogis'}</td>
                  <td className="admin-nav-date">{formatDate(prevNotice.CREATED_AT)}</td>
                </tr>
              )}
              {nextNotice && (
                <tr className="admin-nav-row" onClick={() => { pushState('view'); setViewNotice(nextNotice); }}>
                  <td className="admin-nav-label">다음</td>
                  <td className="admin-nav-title">{pickTitle(nextNotice)}</td>
                  <td className="admin-nav-author">{nextNotice.AUTHOR || 'medilogis'}</td>
                  <td className="admin-nav-date">{formatDate(nextNotice.CREATED_AT)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </AdminShell>
    );
  }

  // 검색 필터

  const adminFiltered = notices.filter(n => {
    if (adminCat !== 'ALL' && (n.CATEGORY || 'PRESS') !== adminCat) return false;
    if (!adminSearch) return true;
    const keyword = adminSearch.toLowerCase();
    if (adminSearchType === 'title') return (n.TITLE || '').toLowerCase().includes(keyword);
    if (adminSearchType === 'content') return (n.CONTENT || '').toLowerCase().includes(keyword);
    return true;
  });

  const adminTotalPages = Math.max(1, Math.ceil(adminFiltered.length / ADMIN_PAGE_SIZE));
  const adminStartIdx = (adminPage - 1) * ADMIN_PAGE_SIZE;
  const adminPageItems = adminFiltered.slice(adminStartIdx, adminStartIdx + ADMIN_PAGE_SIZE);
  const adminNormalItems = adminFiltered.filter(x => !x.IS_PINNED);

  const subjectMap = { product: '제품 문의', pricing: '가격/도입 문의', demo: '데모 요청', partnership: '제휴/협력', other: '기타' };

  // 문의 상세보기
  if (viewInquiry) {
    return (
      <AdminShell {...shellProps} title="문의 보기">
        <div className="admin-detail">
          <div className="admin-detail-header">
            <h3>{subjectMap[viewInquiry.SUBJECT] || viewInquiry.SUBJECT || '문의'}</h3>
            <div className="admin-detail-meta">
              <span>이름: {viewInquiry.NAME}</span>
              <span>이메일: {viewInquiry.EMAIL || '-'}</span>
              <span>연락처: {viewInquiry.PHONE || '-'}</span>
              <span>날짜: {formatDate(viewInquiry.CREATED_AT)}</span>
            </div>
          </div>
          <div className="admin-detail-content">
            <p>{viewInquiry.MESSAGE || '(내용 없음)'}</p>
          </div>
          <div className="admin-actions" style={{justifyContent:'flex-end', alignItems:'center'}}>
            {viewInquiry.STATUS === 0 && (
              <button className="admin-btn-primary" onClick={() => {
                fetch(`/api/admin/inquiry/${viewInquiry.IDX}/confirm`, { method: 'PUT', headers })
                  .then(res => res.json())
                  .then(data => {
                    if (data.success) {
                      loadInquiries();
                      setViewInquiry(null);
                    }
                  });
              }}>확인 완료</button>
            )}
            {viewInquiry.STATUS === 1 && <span className="admin-confirmed-label">확인됨</span>}
            <button className="admin-btn-cancel" onClick={() => setViewInquiry(null)}>목록으로</button>
            <button className="admin-btn-cancel admin-btn-del" onClick={() => handleDeleteInquiry(viewInquiry.IDX)}>삭제</button>
          </div>
        </div>
      </AdminShell>
    );
  }

  // 목록 화면
  return (
    <AdminShell
      {...shellProps}
      title={NAV_TITLE[adminTab] || '관리'}
      actions={adminTab === 'notice' && (
        <button className="admin-btn-primary" onClick={() => { pushState('edit'); setForm(EMPTY_FORM); setEditMode('new'); }}>글쓰기</button>
      )}
    >
      {adminTab === 'notice' && (
      <>
      <form className="admin-search" onSubmit={(e) => { e.preventDefault(); setAdminPage(1); }}>
        <select value={adminCat} onChange={e => { setAdminCat(e.target.value); setAdminPage(1); }}>
          <option value="ALL">전체 ({notices.length})</option>
          <option value="PRESS">보도자료 ({notices.filter(x => (x.CATEGORY || 'PRESS') === 'PRESS').length})</option>
          <option value="NOTICE">공지사항 ({notices.filter(x => x.CATEGORY === 'NOTICE').length})</option>
        </select>
        <select value={adminSearchType} onChange={e => setAdminSearchType(e.target.value)}>
          <option value="title">제목</option>
          <option value="content">내용</option>
        </select>
        <input type="text" value={adminSearch} onChange={e => setAdminSearch(e.target.value)} placeholder="" />
        <button type="submit">검색</button>
      </form>
      <div className="ad-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>번호</th>
              <th>제목</th>
              <th>작성자</th>
              <th>날짜</th>
              <th>조회</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {adminPageItems.length === 0 ? (
              <tr><td colSpan="7" style={{ textAlign: 'center', padding: 40, color: '#888' }}>글이 없습니다.</td></tr>
            ) : adminPageItems.map((n) => {
              const normalIdx = adminNormalItems.indexOf(n);
              const normalNum = normalIdx >= 0 ? adminNormalItems.length - normalIdx : 0;
              return (
              <tr key={n.IDX} className={n.IS_PINNED ? 'admin-row-pinned' : ''}>
                <td>{n.IS_PINNED ? <span className="badge-pin">공지</span> : normalNum}</td>
                <td className="admin-td-title" onClick={() => { pushState('view'); setViewNotice(n); }}>{pickTitle(n)}</td>
                <td>{n.AUTHOR}</td>
                <td>{formatDate(n.CREATED_AT)}</td>
                <td>{n.VIEW_COUNT}</td>
                <td>
                  <button className="admin-btn-sm" onClick={() => { pushState('edit'); handleEdit(n); }}>수정</button>
                  <button className="admin-btn-sm admin-btn-del" onClick={() => handleDelete(n.IDX)}>삭제</button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {adminTotalPages > 1 && (
        <div className="admin-pagination">
          <button onClick={() => setAdminPage(1)} disabled={adminPage === 1}>&laquo;</button>
          <button onClick={() => setAdminPage(p => Math.max(1, p - 1))} disabled={adminPage === 1}>&lsaquo;</button>
          {Array.from({ length: adminTotalPages }, (_, i) => i + 1).map(p => (
            <button key={p} className={adminPage === p ? 'active' : ''} onClick={() => setAdminPage(p)}>{p}</button>
          ))}
          <button onClick={() => setAdminPage(p => Math.min(adminTotalPages, p + 1))} disabled={adminPage === adminTotalPages}>&rsaquo;</button>
          <button onClick={() => setAdminPage(adminTotalPages)} disabled={adminPage === adminTotalPages}>&raquo;</button>
        </div>
      )}
      </>
      )}
      {adminTab === 'inquiry' && (
      <div className="ad-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>번호</th>
              <th>상태</th>
              <th>유형</th>
              <th>이름</th>
              <th>이메일</th>
              <th>연락처</th>
              <th>날짜</th>
            </tr>
          </thead>
          <tbody>
            {inquiries.length === 0 ? (
              <tr><td colSpan="8" style={{ textAlign: 'center', padding: 40, color: '#888' }}>문의가 없습니다.</td></tr>
            ) : inquiries.map((inq, i) => (
              <tr key={inq.IDX} className={`admin-inquiry-row ${inq.STATUS === 0 ? 'unread' : ''}`} onClick={() => { pushState('inquiry'); setViewInquiry(inq); }}>
                <td>{inquiries.length - i}</td>
                <td>{inq.STATUS === 0 ? <span className="admin-status-new">미확인</span> : <span className="admin-status-done">확인</span>}</td>
                <td>{subjectMap[inq.SUBJECT] || inq.SUBJECT || '-'}</td>
                <td>{inq.NAME}</td>
                <td>{inq.EMAIL || '-'}</td>
                <td>{inq.PHONE || '-'}</td>
                <td>{formatDate(inq.CREATED_AT)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
      {adminTab === 'popup' && (
        <PopupAdmin token={token} onUnauthorized={handleLogout} />
      )}
      {adminTab === 'content' && <AdminContent token={token} />}
      {adminTab === 'access' && <AdminAccess token={token} />}
      {adminTab === 'password' && <AdminPassword token={token} />}
    </AdminShell>
  );
}

export default Admin;
